import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Plus, Search, FileText, Clock, Calendar, User, Filter } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const Referrals = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchReferrals();
  }, []);

  const fetchReferrals = async () => {
    try {
      const endpoint = user?.role === 'patient' ? '/api/referrals/my-referrals' : '/api/referrals';
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setReferrals(data.referrals || []);
      }
    } catch (error) {
      console.error('Error fetching referrals:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      scheduled: 'bg-blue-100 text-blue-800 border-blue-200',
      in_progress: 'bg-purple-100 text-purple-800 border-purple-200',
      completed: 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredReferrals = referrals.filter(ref => {
    const matchesSearch = ref.patient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          ref.procedure?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || ref.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading referrals...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-[#0A1628] mb-2 flex items-center">
            <FileText className="h-8 w-8 mr-3 text-[#D4AF37]" />
            Referrals & Cases
          </h1>
          <p className="text-gray-600">Manage patient referrals and track their progress</p>
        </div>
        {user?.role !== 'patient' && (
          <Button 
            className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
            onClick={() => navigate('/referrals/new')}
          >
            <Plus className="h-4 w-4 mr-2" />
            New Referral
          </Button>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            placeholder="Search referrals..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          className="px-3 py-2 border rounded-md"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="scheduled">Scheduled</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredReferrals.length > 0 ? filteredReferrals.map((referral) => (
          <Card 
            key={referral.id} 
            className="hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => navigate(`/referrals/${referral.id}`)}
          >
            <CardHeader>
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <CardTitle className="text-lg mb-1">{referral.patient_name}</CardTitle>
                  <p className="text-sm text-gray-600">{referral.procedure}</p>
                </div>
                <Badge className={getStatusColor(referral.status)}>
                  {referral.status?.replace('_', ' ')}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center text-sm text-gray-600">
                <User className="h-4 w-4 mr-2" />
                {referral.referring_doctor_name}
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <Calendar className="h-4 w-4 mr-2" />
                {new Date(referral.created_at).toLocaleDateString()}
              </div>
              {referral.urgency === 'urgent' && (
                <Badge className="bg-red-100 text-red-800">Urgent</Badge>
              )}
            </CardContent>
          </Card>
        )) : (
          <div className="col-span-full">
            <Card>
              <CardContent className="py-24 text-center">
                <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No Referrals Yet</h3>
                <p className="text-gray-500 mb-6">Create your first referral to get started</p>
                {user?.role !== 'patient' && (
                  <Button 
                    className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
                    onClick={() => navigate('/referrals/new')}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create Referral
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default Referrals;